import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

import Training from "../../entities/Training";
import i18n from "../../i18n";
import RecordStatus from "../../util/config/recordStatus";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import ResponseBaseInterval from "../types/ResponseBaseInterval";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader, getWorkerId } from "../users/auth";
import { getEnumValueByKeySafe } from "components/utils";
import { extractCollection } from "./normalizers";
import { getDerivedDate, getDerivedName } from "./normalizers.derived";

// RESPONSE DECLARATIONS //
interface PersonTrainingResponse extends ResponseBaseInterval {
    Days: number;
    Hours: number;
    LocationName: string;
    Name: string;
    PersonId: number;
    RecordStatus: keyof typeof RecordStatus;
    SupplierName: string;
    Training: TrainingResponse;
    TrainingId: number;
    TrainingTypeId: number;
    TypeName: string;
}

interface TrainingResponse extends ResponseBaseInterval {
    Days: number;
    Hours: number;
    Location: LocationResponse;
    LocationId: number;
    Name: string;
    RecordStatus: RecordStatus;
    Supplier: SupplierResponse;
    SupplierId: number;
    Type: TrainingTypeResponse;
    TypeId: number;
}

interface LocationResponse extends ResponseBase {
    Name: string;
}

interface SupplierResponse extends ResponseBase {
    Name: string;
}

interface TrainingTypeResponse extends ResponseBase {
    Name: string;
}

// SLICE //
interface State extends SliceCollectionBase<Training> { }

const initialState: State = {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
};

const slice = createSlice({
    name: "trainings",
    initialState,
    reducers: {
        trainingsRequested: (trainings) => {
            trainings.loading = true;
        },
        trainingsReceived: (trainings, action: PayloadAction<unknown>) => {
            trainings.list = schema(extractCollection<PersonTrainingResponse>(action.payload));
            trainings.loading = false;
            trainings.lastFetch = Date.now();
        },
        trainingsRequestFailed: (trainings, action: PayloadAction<ApiCallFailedPayload>) => {
            trainings.loading = false;
            trainings.error = action.payload;
        }
    }
});

const {
    trainingsRequested,
    trainingsReceived,
    trainingsRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getTrainings = () => async (dispatch: AppDispatch, getState: () => AppState) => {
    const { lastFetch, loading } = getState().entities.trainings;

    if (loading) {
        return;
    }

    const diffInMinutes = moment().diff(moment(lastFetch), "minutes");
    if (diffInMinutes < parseInt((window._env_.REACT_APP_COMMON_REQUEST_INTERVAL || process.env.REACT_APP_COMMON_REQUEST_INTERVAL)!, 10)) {
        return;
    }

    const workerId = getWorkerId(getState());
    const url = `/api/classificator/person-trainings?personId=${workerId}`;

    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: trainingsRequested.type,
            onSuccess: trainingsReceived.type,
            onError: trainingsRequestFailed.type
        }));
};

// NORMALIZATION //
const schema = (payload: PersonTrainingResponse[]): Training[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map((resp: PersonTrainingResponse): Training => (
        {
            Days: +(resp.Days === 0 ? resp.Training?.Days?.toFixed(2) : resp.Days?.toFixed(2)) || 0,
            EndDate: getDerivedDate(resp.EndDate, resp.Training?.EndDate),
            Hours: +(resp.Hours === 0 ? resp.Training?.Hours?.toFixed(2) : resp.Hours?.toFixed(2)) || 0,
            Id: resp.Id,
            LocationName: getDerivedName(resp.LocationName, resp.Training?.Location),
            Name: getDerivedName(resp.Name, resp.Training),
            PersonId: resp.PersonId,
            RecordStatus: getEnumValueByKeySafe(RecordStatus, resp.RecordStatus),
            StartDate: getDerivedDate(resp.StartDate, resp.Training?.StartDate),
            SupplierName: getDerivedName(resp.SupplierName, resp.Training?.Supplier),
            TypeId: resp.TrainingTypeId,
            TypeName: getDerivedName(resp.TypeName, resp.Training?.Type)
        }
    ))
};
